// @file Emits the PostHog "Signed up" event that completes the landside activation funnel.
//
// The landside pages already dual-emit their CTA clicks to the main Padlet PostHog project
// (see bits/landside_analytics), so the funnel reaches "Clicked CTA" (cta = sign_up) but has no
// completion step. This captures that completion through the shared PostHog helper
// (bits/posthog_analytics), so the transport behavior (beacon, error reporting) can never drift
// from the rest of the funnel and the event carries the browser's persisted anonymous
// distinct_id, joining end-to-end.
import { captureException } from '@@/bits/error_tracker'
import { getSearchParams } from '@@/bits/location'
import { capturePostHogEvent } from '@@/bits/posthog_analytics'

// Matches the RudderStack 'Signed up' action so both destinations read identically.
const SIGNED_UP_POSTHOG_EVENT = 'Signed up'

// Upper bound on how long signup completion waits for the capture before redirecting. The
// PostHog client is warmed on the signup page's mount (warmPostHog in AuthSignupApp), so the
// capture normally settles immediately; the bound only bites when the lazy client load stalls,
// and telemetry must never leave signup stuck ahead of navigateTo.
const SIGNUP_CONVERSION_CAPTURE_TIMEOUT_MS = 1000

// The capture's own error-reporting source, so signup-page telemetry is never attributed to
// the landside module it used to be routed through.
const SIGNUP_CONVERSION_SOURCE = 'signup_conversion'

/**
 * Reduce an analytics URL to origin + path. The query string and hash of a referring page
 * can carry tokens or other PII, and those must never reach PostHog. Non-http(s) schemes
 * (mailto:, data:, …) parse successfully but put the payload in pathname with origin
 * "null"; the `referrer` query param is user-controllable, so those must fail closed too.
 * An unparseable value is also dropped.
 */
function sanitizeAnalyticsUrl(value: string): string {
  if (!value) return ''
  try {
    const url = new URL(value)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return ''
    return url.origin + url.pathname
  } catch {
    return ''
  }
}

/**
 * Where the user arrived at the signup page from. `referrer` is the browser's document
 * referrer, sanitized to origin + path (see sanitizeAnalyticsUrl), which catches same-site
 * navigations like the login page's sign-up button; `source_url` is the explicit `referrer`
 * query param our own links carry (e.g. dashboard "make" links), also sanitized to origin +
 * path. Empty values are omitted from the event.
 */
function signupConversionSourceProperties(): Record<string, string> {
  const { referrer: sourceUrl } = getSearchParams()
  const referrer = sanitizeAnalyticsUrl(document.referrer)
  const sanitizedSourceUrl = sanitizeAnalyticsUrl(sourceUrl)
  return {
    ...(referrer ? { referrer } : {}),
    ...(sanitizedSourceUrl ? { source_url: sanitizedSourceUrl } : {}),
  }
}

/**
 * Capture the "Signed up" conversion via the shared PostHog helper (beacon + skip-the-batch,
 * so the event survives the post-signup redirect). Resolves once the beacon is queued or
 * SIGNUP_CONVERSION_CAPTURE_TIMEOUT_MS elapses, whichever comes first, and never rejects — a
 * stalled or failed capture is reported but must not delay signup. The timeout is cleared when
 * the capture settles first, so no timer is left pending behind a completed signup.
 */
function captureSignedUpConversion(properties: Record<string, unknown> = {}): Promise<void> {
  // `category` is spread last so a stray `category` in props can never override it.
  const capture = capturePostHogEvent(
    SIGNED_UP_POSTHOG_EVENT,
    { ...properties, category: 'Auth' },
    SIGNUP_CONVERSION_SOURCE,
  )
  return new Promise<void>((resolve) => {
    const timeout = setTimeout(() => {
      // A stalled capture must not block signup — but it must not be invisible either.
      captureException(new Error('signup conversion capture timed out'), {
        context: { source: SIGNUP_CONVERSION_SOURCE, event: SIGNED_UP_POSTHOG_EVENT },
      })
      resolve()
    }, SIGNUP_CONVERSION_CAPTURE_TIMEOUT_MS)
    const settleEarly = (): void => {
      clearTimeout(timeout)
      resolve()
    }
    capture.then(settleEarly, settleEarly)
  })
}

export { captureSignedUpConversion, SIGNED_UP_POSTHOG_EVENT, signupConversionSourceProperties }
